import { getCookieByName, setCookie } from '@clientShared/utils';
import {
  Container,
  DecisionApiRequest,
  GetPlacements,
  GetPlacementsRequest,
  KevelPlaceholder,
  Placement,
  RenderedProductCardsCollection,
} from '@clientWebServices/kevel.service';

export default class Kevel {
  static Instance: Kevel;
  public userId: string | null = null;
  private static COOKIE_NAME = 'media_uid';

  constructor() {
    if (!Kevel.Instance) {
      this.userId = this.ensureUserId();
      this.exposeSetUserIdToWindow();

      return (Kevel.Instance = this);
    }
    return Kevel.Instance;
  }

  // #region Private Methods
  private ensureUserId(): string {
    const cookieUserId = getCookieByName(Kevel.COOKIE_NAME);
    if (cookieUserId) {
      return cookieUserId;
    }

    const workerUserId = this.getUserIdFromWorker();
    if (workerUserId) {
      return workerUserId;
    }

    const newUserId = crypto.randomUUID();
    return newUserId;
  }

  private getUserIdFromWorker(): string | undefined {
    return document.querySelector('[data-media-banner-identifier]')?.getAttribute('data-media-banner-identifier');
  }

  private setUserIdCookie(userId: string): void {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1); // Add 1 year

    setCookie(userId, Kevel.COOKIE_NAME, date, 'Strict');
  }

  // Makes the setUserIdCookie method available globally so it can be triggered in OptanonWrapper after cookie consent is given
  private exposeSetUserIdToWindow(): void {
    (window as any).KevelSetUserIdCookie = () => {
      Kevel.Instance.setUserIdCookie(this.userId);
    };
  }

  private getKevelPlaceholders = (): KevelPlaceholder[] => {
    return Array.from(document.querySelectorAll('[data-kevel-placement][data-contains-data="false"]') as NodeListOf<HTMLElement>)
      .map((div) => {
        const divName = div.dataset.kevelDivName;

        const listName = div.parentElement?.getAttribute('data-list-name') ?? null;

        const productCardType = +div.dataset.productCardType;
        if (!divName) return null;

        const adTypes = this.getAdtypes(div);
        if (adTypes.length === 0) return null;

        const zones = this.getZones(div);
        const count = this.getCount(div);
        const keywords = this.getKeywords(div);

        return {
          listName,
          divName,
          productCardType,
          adTypes,
          zones,
          count,
          element: div,
          keywords,
        } as KevelPlaceholder;
      })
      .filter((item): item is KevelPlaceholder => item !== null);
  };

  private getAdtypes = (div: HTMLElement): number[] => {
    return (
      div.dataset.adtypes
        ?.split(',')
        .map((t) => parseInt(t.trim(), 10))
        .filter(Number.isFinite) ?? []
    );
  };

  private getKeywords = (div: HTMLElement): string[] => {
    return div.dataset.keywords?.split(',')?.map((k) => k?.trim()) ?? [];
  };

  private getZones = (div: HTMLElement): number[] => {
    return (
      div.dataset.zones
        ?.split(',')
        .map((t) => parseInt(t.trim(), 10))
        .filter(Number.isFinite) ?? []
    );
  };

  private getCount = (div: HTMLElement): number => +div.dataset.count || 0;

  private createKevelRequestData = (kevelPlaceholders: KevelPlaceholder[]): GetPlacementsRequest => {
    const placements: Placement[] = kevelPlaceholders.map((div) => ({
      divName: div.divName,
      adTypes: div.adTypes,
      zoneIds: div.zones,
      count: div.count,
    }));

    const containers: Container[] = kevelPlaceholders.map((div) => ({
      listName: div.listName,
      divName: div.divName,
      productCardType: div.productCardType,
    }));

    const requestData: DecisionApiRequest = {
      user: { key: this.userId },
      consent: { gdpr: this.hasConsentedToCookie() },
      placements: placements,
      keywords: [...new Set(kevelPlaceholders.flatMap((div) => div.keywords))],
      url: window.location.pathname,
    };

    return { decisionApiRequest: requestData, containers };
  };

  private fetchPlacements = async (requestData: GetPlacementsRequest): Promise<RenderedProductCardsCollection[]> => {
    try {
      const response = await GetPlacements(requestData);
      if (response.status !== 200) return null;
      return response.data?.productCardLists;
    } catch (error) {
      console.error('Error fetching placements:', error);
      return null;
    }
  };

  private replaceContent = (productCardResponse: RenderedProductCardsCollection[], returnReplacedContent: boolean, insert: boolean): HTMLElement[] => {
    let replacedContainers: HTMLElement[] = [];
    productCardResponse.forEach((element: RenderedProductCardsCollection) => {
      if (element.partials?.length === 0) {
        const cardPlaceholders = document.querySelectorAll(`[data-container="product-card"].placeholder`);

        cardPlaceholders.forEach((cardPlaceholder) => {
          cardPlaceholder?.classList?.remove('placeholder');
        });
        return;
      }
      const container: HTMLElement | null = document.querySelector(`[data-kevel-div-name="${element.containerId}"]`);
      if (!container) return;

      let contentHtml = '';
      element.partials.forEach((partialHtml) => {
        if (insert) {
          container.insertAdjacentHTML('beforeend', partialHtml);
        } else {
          contentHtml += partialHtml;
        }
      });

      if (!insert) {
        container.innerHTML = contentHtml;
      }

      container.setAttribute('data-contains-data', 'true');
      if (returnReplacedContent) {
        replacedContainers.push(container);
      }
    });

    return replacedContainers;
  };
  // #endregion

  //#region Private Methods
  replaceWithKevelData = async (returnReplacedContent: boolean = false, insert: boolean = false): Promise<HTMLElement[]> => {
    const kevelPlaceholders = this.getKevelPlaceholders();

    if (kevelPlaceholders.length === 0) return [];

    const requestData = this.createKevelRequestData(kevelPlaceholders);
    if (!requestData) return [];

    const placementResponse = await this.fetchPlacements(requestData);

    if (!placementResponse) return [];

    return this.replaceContent(placementResponse, returnReplacedContent, insert);
  };

  getUserId(): string {
    return this.userId ?? this.ensureUserId();
  }

  hasConsentedToCookie(): boolean {
    return !!getCookieByName(Kevel.COOKIE_NAME);
  }
  //#endregion
}

export enum KevelContainerSelector {
  SearchFlyoutContainer = '[data-container="kevel-search-flyout-container"]',
  SearchFlyoutProducts = '[data-container="kevel-search-flyout-products"]',
}
